import { type AudioClip } from '../audio/AudioBackend';
import { type Renderer } from '../rendering/Renderer';
import { type Texture, type TextureSource } from '../rendering/Texture';
import { AssetCache } from './AssetCache';
import { fetchBytes, loadImageElement, type BytesLoad, type ImageLoad } from './AssetLoaders';

/** The kinds of asset a manifest entry can name. */
export const AssetKind = {
  /** An image, uploaded to the renderer as a {@link Texture}. */
  Texture: 'texture',
  /** A sound, decoded into an {@link AudioClip}. */
  Audio: 'audio',
} as const;

/** One of the {@link AssetKind} values. */
export type AssetKind = (typeof AssetKind)[keyof typeof AssetKind];

/** One line of a manifest: what to load, from where, and as what. */
export interface AssetEntry {
  /** Key the loaded asset is stored under; what a component and `audio.play` refer to. */
  readonly key: string;
  /** URL the asset is fetched from, already resolved by whoever owns the file. */
  readonly url: string;
  /** What to turn the bytes into. */
  readonly kind: AssetKind;
}

/** Called as a manifest load progresses, for a host that wants a loading bar. */
export type AssetProgressListener = (loaded: number, total: number) => void;

/** Turns encoded audio bytes into a clip; the audio manager supplies this. */
export interface AudioDecoder {
  /** Decodes one sound. */
  decode(bytes: ArrayBuffer): Promise<AudioClip>;
}

/** Construction options for {@link AssetManager}. */
export interface AssetManagerOptions {
  /** Backend textures are uploaded to. */
  readonly renderer: Renderer;
  /** Where audio bytes are decoded; normally the `AudioManager`. Without it, audio loads fail. */
  readonly audio?: AudioDecoder | undefined;
  /** Overrides image loading, so the manager can be driven with no network and no DOM. */
  readonly loadImage?: ImageLoad | undefined;
  /** Overrides byte fetching, for the same reason. */
  readonly loadBytes?: BytesLoad | undefined;
}

/**
 * Owns every loaded asset, keyed by the string the rest of the engine refers to it by.
 *
 * The key indirection is the point. A component holds `'car'`, not a `Texture`, so it stays
 * serializable, survives a round trip through the editor and does not pin a GPU resource alive. This
 * is the only object that knows a key maps to a URL and a decoded thing.
 *
 * Nothing loads twice: a key already loaded returns instantly, and a key already in flight hands back
 * the same promise rather than firing a second request.
 */
export class AssetManager {
  readonly #renderer: Renderer;
  readonly #audio: AudioDecoder | undefined;
  readonly #loadImage: ImageLoad;
  readonly #loadBytes: BytesLoad;
  readonly #textures = new AssetCache<Texture>();
  readonly #clips = new AssetCache<AudioClip>();

  #disposed = false;

  constructor(options: AssetManagerOptions) {
    this.#renderer = options.renderer;
    this.#audio = options.audio;
    this.#loadImage = options.loadImage ?? loadImageElement;
    this.#loadBytes = options.loadBytes ?? fetchBytes;
  }

  /** Number of assets loaded, across every kind. */
  get size(): number {
    return this.#textures.size + this.#clips.size;
  }

  /** Keys of every loaded texture, as a fresh array. What the editor's asset browser lists. */
  get textureKeys(): readonly string[] {
    return this.#textures.keys;
  }

  /** Keys of every loaded sound, as a fresh array. */
  get audioKeys(): readonly string[] {
    return this.#clips.keys;
  }

  /**
   * Loads a whole manifest, reporting progress as each entry lands.
   *
   * Entries load concurrently, because a manifest is a set of independent files and loading them in
   * sequence would make a game's first frame wait for the slowest chain rather than the slowest file.
   * One failure rejects the whole call; a game that cannot show its car should not start.
   */
  async load(entries: readonly AssetEntry[], onProgress?: AssetProgressListener): Promise<void> {
    let loaded = 0;
    const total = entries.length;
    onProgress?.(loaded, total);

    await Promise.all(
      entries.map(async (entry) => {
        await this.loadEntry(entry);
        loaded += 1;
        onProgress?.(loaded, total);
      }),
    );
  }

  /** Loads one manifest entry. */
  async loadEntry(entry: AssetEntry): Promise<void> {
    switch (entry.kind) {
      case AssetKind.Texture:
        await this.loadTexture(entry.key, entry.url);
        return;
      case AssetKind.Audio:
        await this.loadAudio(entry.key, entry.url);
        return;
      default:
        throw new Error(`Asset "${entry.key}" has an unknown kind.`);
    }
  }

  /** Loads an image and uploads it as a texture under `key`. */
  async loadTexture(key: string, url: string): Promise<Texture> {
    return this.#textures.load(key, async () => this.#renderer.createTexture(await this.#loadImage(url)));
  }

  /** Loads and decodes a sound under `key`. Throws when no audio decoder was wired up. */
  async loadAudio(key: string, url: string): Promise<AudioClip> {
    const audio = this.#audio;
    if (audio === undefined) {
      throw new Error(`Cannot load the audio asset "${key}": this asset manager has no audio decoder.`);
    }
    return this.#clips.load(key, async () => audio.decode(await this.#loadBytes(url)));
  }

  /** The texture under this key, or undefined when nothing is loaded for it. */
  getTexture(key: string): Texture | undefined {
    return this.#textures.get(key);
  }

  /** Like {@link AssetManager.getTexture} but throws, for content a game cannot run without. */
  requireTexture(key: string): Texture {
    const texture = this.#textures.get(key);
    if (texture === undefined) {
      throw new Error(`No texture is loaded under the asset key "${key}".`);
    }
    return texture;
  }

  /** The clip under this key, or undefined when nothing is loaded for it. */
  getAudio(key: string): AudioClip | undefined {
    return this.#clips.get(key);
  }

  /** Like {@link AssetManager.getAudio} but throws, for content a game cannot run without. */
  requireAudio(key: string): AudioClip {
    const clip = this.#clips.get(key);
    if (clip === undefined) {
      throw new Error(`No sound is loaded under the asset key "${key}".`);
    }
    return clip;
  }

  /**
   * Uploads an image the caller already holds and stores it under `key`.
   *
   * The way anything generated at runtime, such as a rasterized label, joins the same namespace files
   * are loaded into, so a component refers to both the same way.
   */
  addTexture(key: string, source: TextureSource): Texture {
    const existing = this.#textures.get(key);
    if (existing !== undefined) {
      existing.dispose();
    }
    return this.#textures.set(key, this.#renderer.createTexture(source));
  }

  /** Returns the texture under `key`, uploading one from `createSource` only if none exists yet. */
  getOrCreateTexture(key: string, createSource: () => TextureSource): Texture {
    return this.#textures.get(key) ?? this.#textures.set(key, this.#renderer.createTexture(createSource()));
  }

  /** Whether anything is loaded under this key. */
  has(key: string): boolean {
    return this.#textures.has(key) || this.#clips.has(key);
  }

  /** Releases the texture under `key` and reports whether one was there. */
  unloadTexture(key: string): boolean {
    const texture = this.#textures.delete(key);
    texture?.dispose();
    return texture !== undefined;
  }

  /** Releases every texture and forgets every clip. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;

    for (const texture of this.#textures.values) {
      texture.dispose();
    }
    this.#textures.clear();
    this.#clips.clear();
  }
}
